(function (global) {
  function parseNum(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  function parseMs(v) {
    if (v == null || v === "") return NaN;
    return new Date(v).getTime();
  }

  /**
   * @param {object} campaign - { votes, goal, start_date, end_date, is_completed }
   * @param {number} [nowMs]
   * @returns {'goal_reached'|'upcoming'|'expired'|'live'}
   */
  function getStatus(campaign, nowMs) {
    campaign = campaign || {};
    var now = typeof nowMs === "number" ? nowMs : Date.now();
    var votes = parseNum(campaign.votes);
    var goal = parseNum(campaign.goal);
    var start = parseMs(campaign.start_date);
    var end = parseMs(campaign.end_date);

    if (!isNaN(end) && now > end) return "expired";

    if (goal > 0 && votes >= goal) return "goal_reached";

    if (isNaN(start) || isNaN(end)) return "live";
    if (now < start) return "upcoming";
    return "live";
  }

  function getCtaLabel(status) {
    if (status === "upcoming") return "Voting opens soon";
    if (status === "goal_reached") return "Goal reached";
    if (status === "expired") return "Voting Completed";
    return null;
  }

  function getRejectMessage(status) {
    if (status === "upcoming") return "Voting has not started yet.";
    if (status === "expired") return "Voting has ended.";
    if (status === "goal_reached") return "This campaign has reached its goal.";
    return null;
  }

  /** Compact status line for cards / concept list (not the full countdown). */
  function getTimeLabel(campaign, nowMs) {
    var status = getStatus(campaign, nowMs);
    var now = typeof nowMs === "number" ? nowMs : Date.now();
    if (status === "goal_reached") return "Goal reached";
    if (status === "expired") return "Voting ended";
    if (status === "upcoming") {
      try {
        return (
          "Opens " +
          new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }).format(new Date(campaign.start_date))
        );
      } catch (e) {
        return "Opens soon";
      }
    }
    var end = parseMs(campaign.end_date);
    if (isNaN(end)) return "";
    var remaining = end - now;
    if (remaining <= 0) return "Voting ended";
    var days = Math.ceil(remaining / 86400000);
    if (days > 1) return days + " days left";
    var hours = Math.ceil(remaining / 3600000);
    if (hours > 1) return hours + " hours left";
    var mins = Math.max(1, Math.ceil(remaining / 60000));
    return mins + (mins === 1 ? " minute left" : " minutes left");
  }

  /** Percent of goal with two decimals, e.g. "26.00". */
  function formatProgressPercent(votes, goal) {
    var v = parseNum(votes);
    var g = parseNum(goal);
    if (g <= 0) return "0.00";
    return ((v / g) * 100).toFixed(2);
  }

  /** Bar width 0–100 from votes/goal (goal_reached forces 100). */
  function getProgressBarWidth(campaign, nowMs) {
    campaign = campaign || {};
    if (getStatus(campaign, nowMs) === "goal_reached") return 100;
    var g = parseNum(campaign.goal);
    if (g <= 0) return 0;
    return Math.min(Math.round((parseNum(campaign.votes) / g) * 100), 100);
  }

  /**
   * Split remaining line for Fundpop-style row.
   * { prefix: 'Time remaining', value: '24 days : 17 hours' }
   */
  function getTimeRemainingParts(campaign, nowMs) {
    var status = getStatus(campaign, nowMs);
    var now = typeof nowMs === "number" ? nowMs : Date.now();
    if (status === "goal_reached") return { prefix: "", value: "Goal reached" };
    if (status === "expired") return { prefix: "", value: "Voting ended" };

    var targetMs = status === "upcoming" ? parseMs(campaign.start_date) : parseMs(campaign.end_date);
    if (isNaN(targetMs)) return { prefix: "", value: "" };

    var remaining = Math.max(0, targetMs - now);
    var days = Math.floor(remaining / 86400000);
    var hours = Math.floor((remaining % 86400000) / 3600000);
    return {
      prefix: status === "upcoming" ? "Starts in" : "Time remaining",
      value: days + " days : " + hours + " hours",
    };
  }

  /**
   * Full remaining line for progress widgets.
   * e.g. "Time remaining 24 days : 17 hours"
   */
  function getTimeRemainingLabel(campaign, nowMs) {
    var parts = getTimeRemainingParts(campaign, nowMs);
    if (!parts.prefix) return parts.value;
    if (!parts.value) return parts.prefix;
    return parts.prefix + " " + parts.value;
  }

  function campaignFromProgressRoot(root) {
    if (!root) return null;
    return {
      votes: root.getAttribute("data-votes"),
      goal: root.getAttribute("data-goal"),
      start_date: root.getAttribute("data-start-date"),
      end_date: root.getAttribute("data-end-date"),
      is_completed: root.getAttribute("data-is-completed"),
    };
  }

  function renderClosedCtaHtml(status, options) {
    options = options || {};
    var shapeClass = options.shape === "pill" ? "!rounded-full !py-3" : "!rounded-sm !py-6";
    var label = getCtaLabel(status) || "Voting Completed";
    return (
      '<button class="w-full relative !text-nowrap !inline-flex !items-center !justify-center !gap-2 !font-medium !h-10 !px-8 ' +
      shapeClass +
      ' !text-sm !text-gray-500 bg-gray-100 cursor-not-allowed" disabled>' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon lucide lucide-check text-gray-400"><path d="M20 6L9 17l-5-5"/></svg>' +
      '<span class="button-text">' +
      label +
      "</span></button>"
    );
  }

  function ctaShapeForProduct(productId) {
    var cta =
      document.getElementById("VotingCta-" + productId) ||
      document.querySelector('[data-voting-cta="' + productId + '"]');
    if (cta && cta.getAttribute("data-cta-shape") === "pill") return "pill";
    var root = document.querySelector('[data-vote-progress-root="' + productId + '"]');
    if (root && root.getAttribute("data-cta-shape") === "pill") return "pill";
    return "square";
  }

  function applyExpiredUi(productId) {
    if (!productId || typeof document === "undefined") return;
    var progress = document.querySelector('[data-vote-progress-root="' + productId + '"]');
    if (progress) {
      progress.setAttribute("data-status", "expired");
      progress.hidden = true;
    }
    var countdown = document.getElementById("VotingCountdown-" + productId);
    if (countdown) {
      countdown.hidden = true;
      countdown.setAttribute("aria-hidden", "true");
      countdown.setAttribute("data-status", "expired");
    }
    var cta =
      document.getElementById("VotingCta-" + productId) ||
      document.querySelector('[data-voting-cta="' + productId + '"]');
    if (cta) {
      cta.innerHTML = renderClosedCtaHtml("expired", { shape: ctaShapeForProduct(productId) });
    }
  }

  var VOTES_API_PREFIX = "https://ranmerch.com/apps/randamu-api/api/products/votes/customer/";

  function shopifyNumericId(value) {
    if (value == null || value === "") return "";
    return String(value).replace(/\D/g, "");
  }

  function normalizeProductIdList(ids) {
    var out = [];
    var seen = {};
    (ids || []).forEach(function (id) {
      var numeric = shopifyNumericId(id);
      if (!numeric || seen[numeric]) return;
      seen[numeric] = true;
      out.push(numeric);
    });
    return out;
  }

  function parseVotesResponse(json) {
    var ids = json && json.productIds;
    return normalizeProductIdList(Array.isArray(ids) ? ids : []);
  }

  function hasVotedProduct(productIds, productId) {
    var numeric = shopifyNumericId(productId);
    if (!numeric) return false;
    return (productIds || []).some(function (id) {
      return shopifyNumericId(id) === numeric;
    });
  }

  function applyHasVotedState(productId, voted) {
    if (typeof window === "undefined") return;
    window.__randamuVoteState = window.__randamuVoteState || {};
    window.__randamuVoteState[productId] = !!voted;
    if (typeof document === "undefined") return;
    var countdown = document.getElementById("VotingCountdown-" + productId);
    if (countdown) countdown.setAttribute("data-has-voted", voted ? "true" : "false");
  }

  function thumbsUpSvg(iconClass) {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon lucide lucide-thumbs-up ' +
      iconClass +
      '"><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/><path d="M7 10v12"/></svg>'
    );
  }

  function voteHandlerForProduct(productId) {
    var cta =
      document.getElementById("VotingCta-" + productId) ||
      document.querySelector('[data-voting-cta="' + productId + '"]');
    if (cta && cta.getAttribute("data-vote-handler") === "openVoteDialog") {
      return "openVoteDialog";
    }
    return "handleVote";
  }

  function renderVoteCtaHtml(productId, options) {
    options = options || {};
    var shapeClass = options.shape === "pill" ? "!rounded-full !py-3" : "!rounded-sm !py-6";
    var handler = options.handler === "openVoteDialog" ? "openVoteDialog" : "handleVote";
    return (
      '<button id="vote-button-' +
      productId +
      '" class="vote-btn w-full relative !text-nowrap !inline-flex !items-center !justify-center !gap-2 !font-medium !h-10 !px-8 ' +
      shapeClass +
      ' !text-sm !text-gray-700 bg-gray-200 hover:bg-gray-300 !transition-all disabled:opacity-70 disabled:cursor-not-allowed" onclick="' +
      handler +
      "('" +
      productId +
      "')\">" +
      thumbsUpSvg("text-gray-600") +
      '<span class="button-text text-gray-700">Vote</span></button>'
    );
  }

  function renderVotedCtaHtml(productId, options) {
    options = options || {};
    var shapeClass = options.shape === "pill" ? "!rounded-full !py-3" : "!rounded-sm !py-6";
    return (
      '<button id="remove-vote-button-' +
      productId +
      '" class="vote-btn w-full relative !text-nowrap !inline-flex !items-center !justify-center !gap-2 !font-medium !h-10 !px-8 ' +
      shapeClass +
      ' !text-sm !text-white bg-primary-light hover:bg-primary !transition-all disabled:opacity-70 disabled:cursor-not-allowed" onclick="handleRemoveVote(\'' +
      productId +
      "')\">" +
      thumbsUpSvg("text-white") +
      '<span class="button-text text-white">Voted</span></button>'
    );
  }

  function hydrateVotedCtas(productIds) {
    if (typeof document === "undefined") return;
    var ids = normalizeProductIdList(productIds);
    for (var i = 0; i < ids.length; i++) {
      var productId = ids[i];
      applyHasVotedState(productId, true);
      var voteBtn = document.getElementById("vote-button-" + productId);
      if (!voteBtn) continue;
      var cta =
        document.getElementById("VotingCta-" + productId) ||
        document.querySelector('[data-voting-cta="' + productId + '"]');
      if (!cta) continue;
      cta.innerHTML = renderVotedCtaHtml(productId, { shape: ctaShapeForProduct(productId) });
    }
  }

  function pendingVoteProductId(btn) {
    if (!btn) return "";
    var fromAttr = btn.getAttribute && btn.getAttribute("data-product-id");
    if (fromAttr) return shopifyNumericId(fromAttr);
    if (btn.id && btn.id.indexOf("vote-button-") === 0) {
      return shopifyNumericId(btn.id.slice("vote-button-".length));
    }
    return "";
  }

  function revealPendingVoteCtas() {
    if (typeof document === "undefined") return;
    var buttons = document.querySelectorAll('[data-vote-status-pending="true"]');
    for (var i = 0; i < buttons.length; i++) {
      var productId = pendingVoteProductId(buttons[i]);
      if (!productId) continue;
      applyHasVotedState(productId, false);
      var cta =
        document.getElementById("VotingCta-" + productId) ||
        document.querySelector('[data-voting-cta="' + productId + '"]');
      if (!cta) continue;
      cta.innerHTML = renderVoteCtaHtml(productId, {
        shape: ctaShapeForProduct(productId),
        handler: voteHandlerForProduct(productId),
      });
    }
  }

  function fetchCustomerVotedProductIds(customerId, fetchImpl) {
    var fetchFn = fetchImpl || (typeof fetch === "function" ? fetch : null);
    if (!customerId || !fetchFn) {
      return Promise.resolve([]);
    }
    return fetchFn(VOTES_API_PREFIX + encodeURIComponent(customerId), {
      headers: { "Content-Type": "application/json" },
    })
      .then(function (response) {
        if (!response || !response.ok) throw new Error("Failed to load votes");
        return response.json();
      })
      .then(function (json) {
        return parseVotesResponse(json);
      });
  }

  function loadAndHydrateVotes(customerId, fetchImpl) {
    return fetchCustomerVotedProductIds(customerId, fetchImpl)
      .then(function (ids) {
        hydrateVotedCtas(ids);
        revealPendingVoteCtas();
        return ids;
      })
      .catch(function () {
        revealPendingVoteCtas();
        return [];
      });
  }

  function watchVotingExpiry() {
    if (typeof document === "undefined") return;
    var nodes = document.querySelectorAll("[data-vote-progress-root]");
    for (var i = 0; i < nodes.length; i++) {
      var root = nodes[i];
      if (root.getAttribute("data-status") === "expired" || root.hidden) continue;
      if (getStatus(campaignFromProgressRoot(root)) !== "expired") continue;
      applyExpiredUi(root.getAttribute("data-vote-progress-root"));
    }
  }

  function updateProgressRoot(root, votes) {
    if (!root) return;
    if (typeof votes === "number" && !isNaN(votes)) {
      root.setAttribute("data-votes", String(Math.max(0, votes)));
    }
    var campaign = campaignFromProgressRoot(root);
    var productId = root.getAttribute("data-vote-progress-root");
    if (getStatus(campaign) === "expired") {
      applyExpiredUi(productId);
      return;
    }
    var raisedEl = productId ? document.getElementById("vote-raised-" + productId) : null;
    var pctEl = productId ? document.getElementById("vote-number-text-" + productId) : null;
    var barEl = productId ? document.getElementById("vote-progress-bar-" + productId) : null;
    var timePrefixEl = productId ? document.getElementById("vote-time-prefix-" + productId) : null;
    var timeValueEl = productId ? document.getElementById("vote-time-value-" + productId) : null;

    var voteCount = parseNum(campaign.votes);
    var goal = parseNum(campaign.goal);
    if (raisedEl) raisedEl.textContent = String(voteCount);
    if (pctEl) pctEl.textContent = formatProgressPercent(voteCount, goal) + "% of Goal";
    if (barEl) barEl.style.width = getProgressBarWidth(campaign) + "%";

    var parts = getTimeRemainingParts(campaign);
    if (timePrefixEl) {
      timePrefixEl.textContent = parts.prefix;
      timePrefixEl.style.display = parts.prefix ? "" : "none";
    }
    if (timeValueEl) timeValueEl.textContent = parts.value;
  }

  function hydrateProgressRoots(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll("[data-vote-progress-root]");
    for (var i = 0; i < nodes.length; i++) {
      updateProgressRoot(nodes[i]);
    }
  }

  var api = {
    getStatus: getStatus,
    getCtaLabel: getCtaLabel,
    getRejectMessage: getRejectMessage,
    getTimeLabel: getTimeLabel,
    formatProgressPercent: formatProgressPercent,
    getProgressBarWidth: getProgressBarWidth,
    getTimeRemainingParts: getTimeRemainingParts,
    getTimeRemainingLabel: getTimeRemainingLabel,
    updateProgressRoot: updateProgressRoot,
    hydrateProgressRoots: hydrateProgressRoots,
    renderClosedCtaHtml: renderClosedCtaHtml,
    renderVoteCtaHtml: renderVoteCtaHtml,
    renderVotedCtaHtml: renderVotedCtaHtml,
    applyExpiredUi: applyExpiredUi,
    watchVotingExpiry: watchVotingExpiry,
    normalizeProductIdList: normalizeProductIdList,
    parseVotesResponse: parseVotesResponse,
    hasVotedProduct: hasVotedProduct,
    applyHasVotedState: applyHasVotedState,
    hydrateVotedCtas: hydrateVotedCtas,
    revealPendingVoteCtas: revealPendingVoteCtas,
    fetchCustomerVotedProductIds: fetchCustomerVotedProductIds,
    loadAndHydrateVotes: loadAndHydrateVotes,
  };

  global.RandamuVotingCampaign = api;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () {
        hydrateProgressRoots();
        watchVotingExpiry();
        setInterval(watchVotingExpiry, 1000);
      });
    } else {
      hydrateProgressRoots();
      watchVotingExpiry();
      setInterval(watchVotingExpiry, 1000);
    }
  }
})(typeof window !== "undefined" ? window : globalThis);
